/* eslint-disable no-async-promise-executor */
import APP from '$/api/api';
import { GetWorkFlowInfoResult, SaveWorkFlowParams } from '$/api/server.types';
import { useOfficialStore } from '$/store/useOfficialStore';
import useSopStore from '$/store/useSopStore';
import { addCommonParams, addCommonHeaders } from '$/api/helpers';
import { getMCPConfigTransform } from '../mcp/mcpService';
import { McpCategoriesType, McpType } from '../mcp/type';
import { NodeExecuteTaskParams, NodeExecuteTaskResult } from '../node/type';

// 根据环境变量设置 getBaseUrl()
const getBaseUrl = (): string => {
  // 根据环境设置默认值
  const { env } = useOfficialStore.getState();
  const ADMIN_URL_MAP: Record<string, string> = {
    development: 'https://preadmin.roboneo.com',
    dev: 'https://preadmin.roboneo.com',
    pre: 'https://preadmin.roboneo.com',
    beta: 'https://betaadmin.roboneo.com',
    release: 'https://admin.roboneo.com',
  };

  return ADMIN_URL_MAP[env] || ADMIN_URL_MAP.release;
};

/**
 * 获取sop节点数据
 *
 * @export
 * @param {string} room_id
 * @return {*}
 */
export async function getWorkFlowInfo(room_id: string) {
  return new Promise<GetWorkFlowInfoResult>(async (resolve, reject) => {
    const { workflowId, workFlowSopInfoApi } = useSopStore.getState();
    const url = new URL(getBaseUrl() + workFlowSopInfoApi);
    url.searchParams.set('workflow_id', workflowId);
    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include', // 携带 cookie
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((resp) => resp.json()).catch(reject);
    if (response?.code === 0) {
      resolve({ name: '', room_id, data: response.data.canvas_data });
      return;
    }
    resolve({} as GetWorkFlowInfoResult);
  });
}

/**
 * 保存sop节点
 *
 * @export
 * @param {SaveWorkFlowParams} data
 * @return {*}
 */
export async function saveWorkFlow(data: SaveWorkFlowParams) {
  const workflow_id = useSopStore.getState().workflowId;
  const response = await fetch(getBaseUrl() + APP.roboneo.api.saveSopWorkFlow, {
    method: 'POST',
    body: JSON.stringify({ ...data, workflow_id }),
    credentials: 'include', // 携带 cookie
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return response;
}

/**
 * sop节点配置
 *
 * @export
 * @return {*}
 */
export async function WorkFlowSopConfig() {
  const workflow_id = useSopStore.getState().workflowId;
  const url = new URL(getBaseUrl() + APP.roboneo.api.workFlowSopConfig);
  url.searchParams.set('workflow_id', workflow_id);
  const app_id = useOfficialStore.getState().appId;
  url.searchParams.set('app_id', app_id);
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include', // 携带 cookie
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((res) => res.json());
  let mcpConfigList: McpType[] = [];
  let mcpCategoriesList: McpCategoriesType[] = [];
  let mcpNameMap: Record<string, string> = {};
  if (response) {
    const { data } = response;
    if (response.code === 0 && data.mcp_list) {
      const result = getMCPConfigTransform(response);
      mcpConfigList = result.mcpConfigList;
      mcpCategoriesList = result.mcpCategoriesList;
      mcpNameMap = result.mcpNameMap;
    }
  }

  return {
    mcpConfigList,
    mcpCategoriesList,
    mcpNameMap,
  };
}

const getCreateNodeExecuteTaskUrl = () => {
  const { env } = useOfficialStore.getState();
  const TASK_URL_MAP: Record<string, string> = {
    development: 'https://pre-engine-internal.roboneo.com',
    dev: 'https://pre-engine-internal.roboneo.com',
    pre: 'https://pre-engine-internal.roboneo.com',
    beta: 'https://beta-engine-internal.roboneo.com',
    release: 'https://engine-internal.roboneo.com',
  };
  return `${TASK_URL_MAP[env]}/roboneo_internal/workflow`;
};

/**
 * 创建节点执行任务
 *
 * @export
 * @param {string} room_id
 * @param {NodeExecuteTaskParams} params
 * @param {string} [passThroughTaskId]
 * @return {*}
 */
export async function createNodeExecuteSopTask(room_id: string, params: NodeExecuteTaskParams, passThroughTaskId?: string, workflow_version?: 'v0' | 'v1' | 'v2') {
  const parameter: any = {
    ...addCommonParams(),
    app_scene: 'roboneoInternal',
    path_scene: 'picchiNodeExecute',
    should_charge: false,
    room_id,
    node_id: params.node_id,
    node_list_array: [[params]],
    task_prefix: 'admin_random_',
  };
  if (passThroughTaskId) {
    parameter.pass_through_task_id = passThroughTaskId;
  }
  const commonHeaders = await addCommonHeaders();
  const response = await fetch(
    getCreateNodeExecuteTaskUrl(),
    {
      method: 'POST',
      body: JSON.stringify(parameter),
      credentials: 'include',
      headers: {
        ...commonHeaders,
        'Content-Type': 'application/json',
      },
    },
  ).then((res) => res.json());
  return new Promise<{
    task_id: string;
    estimated_total_ms: number; // 预估总耗时，单位毫秒
  }>((resolve, reject) => {
    if (response.error_code === 0) {
      resolve(response.parameter.tasks[0]);
    } else {
      reject(response);
    }
  });
}

/**
 * 查询节点执行任务
 *
 * @export
 * @param {string} task_id
 * @return {*}
 */
export async function queryNodeExecuteSopTask(
  task_ids?: string[],
  room_id?: string,
  invalid_node_types?: string[],
  room_ids?: string[],
  page?: number,
  page_size?: number,
) {
  const commonHeaders = await addCommonHeaders();
  const response = await fetch(
    getCreateNodeExecuteTaskUrl(),
    {
      method: 'POST',
      body: JSON.stringify({
        ...addCommonParams(),
        should_charge: false,
        app_scene: 'roboneoInternal',
        path_scene: 'picchiNodeExecuteQuery',
        task_ids: task_ids || [],
        task_prefix: 'admin_random_',
        ...room_id ? { room_id } : {},
        room_id: room_id || '',
        invalid_node_types,
        room_ids,
        page,
        page_size,
      }),
      credentials: 'include',
      headers: {
        ...commonHeaders,
        'Content-Type': 'application/json',
      },
    },
  ).then((res) => res.json());
  return new Promise<NodeExecuteTaskResult>((resolve, reject) => {
    if (response.error_code === 0) {
      resolve(response.parameter);
    } else {
      reject(response);
    }
  });
}
